import React, { useEffect, useState } from 'react';
import {
  updateDoc,
  setDoc,
  doc,
} from 'firebase/firestore';

import {
  firebaseConfig,
  app,
  auth,
  db,
  functions,
  storage,
  storageRef,
  FirebaseFieldValue,
  FirebaseTimestamp,
  firebaseAuth,
  firebaseImport,
} from '@/js/external-js/config/main_config';

import { httpsCallable } from 'firebase/functions';
import { useTenancy, useOrgStructure, useUsers, type User } from '@/contexts/TenancyContext';
import { entraUserService, type EntraUser } from '@/services/entraUserService';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import { Loader2, Pencil, Trash2, UserPlus2, LayoutGrid, Table2, ChevronDown, User as UserIcon, Mail, Building, Shield as ShieldIcon } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faXmark, faUser, faEnvelope, faBuilding, faShield, faUserShield } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { DataTable } from '@/components/ui/datatable';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Building2, Shield } from 'lucide-react';
import { mspService } from '@/services/mspService';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { TourAlertDialog, useTour } from '@/components/tour';
import { useTourAccess } from '@/hooks/useTourAccess';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

// DED-1250: MSP details structure from customer document
interface MspPartnerDetails {
  impersonationAllowed: boolean;
  msp: string;
}

interface MspDetails {
  techPartner?: MspPartnerDetails;
  businessPartner?: MspPartnerDetails;
}

// User management functionality is now handled by TenancyContext

export default function Users() {
  const { activeTenancy } = useTenancy();
  const { users, loading } = useUsers();
  const { hasTourAccess } = useTourAccess();
  const plan = activeTenancy?.plan || 'default';
  const isIsoPlan = plan === 'default' || plan === 'Business Plan' || plan === 'Enterprise Plan';
  // DED-1444: Check if Starter plan to hide ISMS columns
  const isStarterPlan = plan?.toLowerCase().includes('starter');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editDrawer, setEditDrawer] = useState<User | null>(null);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  
  // Entra users state
  const [entraUsers, setEntraUsers] = useState<EntraUser[]>([]);
  const [entraLoading, setEntraLoading] = useState(false);
  const [showEntraUsers, setShowEntraUsers] = useState(true);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [secretExpiry, setSecretExpiry] = useState<{
    secretExpiryDate: string | null;
    daysUntilExpiry: number | null;
    isExpiringSoon: boolean;
    isExpired: boolean;
  } | null>(null);

  // DED-1250: MSP management state
  const [mspDetails, setMspDetails] = useState<MspDetails | null>(null);
  const [editMspUser, setEditMspUser] = useState<User | null>(null);
  const [mspName, setMspName] = useState<string>('');
  const [mspLoading, setMspLoading] = useState(false);

  // DED-1869: Check if MS Entra integration is enabled
  const [isEntraIntegrationEnabled, setIsEntraIntegrationEnabled] = useState(false);
  const [checkingIntegration, setCheckingIntegration] = useState(true);

  // Track the tenancy ID to reset state when it changes
  const [currentTenancyId, setCurrentTenancyId] = useState<string | null>(null);

  // Tour state
  const { setSteps, isTourCompleted, setTourPage } = useTour();
  const [openTour, setOpenTour] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('createUser') === 'true') {
      setTimeout(() => setShowAddModal(true), 500);
    }
  }, []);

  // Reset state when tenancy changes
  useEffect(() => {
    if (activeTenancy?.id && activeTenancy.id !== currentTenancyId) {
      console.log('Tenancy changed, resetting user management state');
      // Reset pagination
      setCurrentPage(1);
      // Clear search term
      setSearchTerm('');
      // Close any open modals or drawers
      setEditUser(null);
      setEditDrawer(null);
      setShowAddModal(false);
      setShowAddDrawer(false);
      // Update current tenancy ID
      setCurrentTenancyId(activeTenancy.id);
      // DED-1250: Reset MSP state
      setMspDetails(null);
      setEditMspUser(null);
      setMspName('');
    }
  }, [activeTenancy?.id, currentTenancyId]);

  // DED-1250: Fetch MSP details when tenancy changes
  useEffect(() => {
    const fetchMspDetails = async () => {
      if (!activeTenancy?.id) return;
      
      try {
        // Fetch customer document to get mspDetails
        const { getDoc, doc } = await import('firebase/firestore');
        const customerRef = doc(db, 'customers', activeTenancy.id);
        const customerSnap = await getDoc(customerRef);
        
        if (customerSnap.exists()) {
          const data = customerSnap.data();
          if (data.mspDetails) {
            setMspDetails(data.mspDetails);
          } else {
            setMspDetails(null);
          }
        }
      } catch (error) {
        console.error('Error fetching MSP details:', error);
      }
    };
    
    fetchMspDetails();
  }, [activeTenancy?.id]);

  // Fetch Entra users when component loads or tenancy changes (only if integration is enabled)
  useEffect(() => {
    const fetchEntraUsers = async () => {
      if (!activeTenancy?.id || !isEntraIntegrationEnabled) return;
      
      setEntraLoading(true);
      try {
        const result = await entraUserService.getEntraUsers(activeTenancy.id);
        if (result) {
          setEntraUsers(result.users);
          setLastSyncTime(new Date(result.summary.syncedAt));
        } else {
          setEntraUsers([]);
          setLastSyncTime(null);
        }
      } catch (error) {
        console.error('Error fetching Entra users:', error);
        setEntraUsers([]);
        setLastSyncTime(null);
      } finally {
        setEntraLoading(false);
      }
    };
    
    fetchEntraUsers();
  }, [activeTenancy?.id, isEntraIntegrationEnabled]);

  // DED-1869: Check if MS Entra User Sync integration is enabled
  useEffect(() => {
    const checkEntraIntegration = async () => {
      if (!activeTenancy?.id) {
        setCheckingIntegration(false);
        return;
      }
      
      setCheckingIntegration(true);
      try {
        const response = await deiterateAPI.GET<{ integrations?: Array<{ id: string; enabled?: boolean }> }>(
          `/v1/integrations`,
          {
            backend: 'thalassa',
            headers: {
              'x-tenancy-id': activeTenancy.id,
              'company-id': activeTenancy.id,
              'customer-id': activeTenancy.id,
            },
          }
        );
        
        const entraIntegration = response?.integrations?.find(i => i.id === 'entra-user-sync');
        setIsEntraIntegrationEnabled(entraIntegration?.enabled === true);
      } catch (error) {
        console.error('Error checking Entra integration status:', error);
        setIsEntraIntegrationEnabled(false);
      } finally {
        setCheckingIntegration(false);
      }
    };
    
    checkEntraIntegration();
  }, [activeTenancy?.id]);

  // Check Entra secret expiry when component loads or tenancy changes
  useEffect(() => {
    const checkSecretExpiry = async () => {
      if (!activeTenancy?.id || !isEntraIntegrationEnabled) return;
      
      try {
        const expiryStatus = await entraUserService.checkSecretExpiry(activeTenancy.id);
        if (expiryStatus) {
          setSecretExpiry(expiryStatus);
        }
      } catch (error) {
        console.error('Error checking Entra secret expiry:', error);
      }
    };
    
    checkSecretExpiry();
  }, [activeTenancy?.id, isEntraIntegrationEnabled]);

  // Tour implementation
  useEffect(() => {
    setTourPage('user-management');
  }, [setTourPage]);

  // Tour configuration for User Management
  useEffect(() => {
    // Only set up tour if user has access
    if (!hasTourAccess) {
      return;
    }

    const steps: any[] = [
      {
        content: (
          <div>
            <h3 className="font-semibold text-lg mb-2">User Management</h3>
            <p>This is your user management page where you can manage all users in your organization.</p>
          </div>
        ),
        selectorId: TOUR_STEP_IDS.USER_MANAGEMENT_PAGE,
        position: "bottom" as const,
      }
    ];

    // Search Bar
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Search Bar</h3>
          <p>This will search for users by name or email.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_MANAGEMENT_SEARCH_BAR,
      position: "bottom" as const,
    });

    // Show Entra Users toggle
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Show Entra Users toggle</h3>
          <p>This will toggle the display of Entra ID users.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_MANAGEMENT_SHOW_ENTRA_TOGGLE,
      position: "bottom" as const,
    });

    // Sync Button
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Sync Button</h3>
          <p>This will sync users from Entra ID.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_MANAGEMENT_SYNC_BUTTON,
      position: "bottom" as const,
    });

    // Add User
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Add User</h3>
          <p>This will open the add user dialog.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_MANAGEMENT_ADD_USER,
      position: "bottom" as const,
    });

    // Table action buttons
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Table action buttons</h3>
          <p>These buttons allow you to edit or delete users.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_MANAGEMENT_TABLE_ACTIONS,
      position: "bottom" as const,
    });

    // Per page
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Per page</h3>
          <p>This will change the number of results per page.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_MANAGEMENT_RESULTS_SELECTOR,
      position: "left" as const,
    });

    // Columns button
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Columns</h3>
          <p>This will open the columns modal to manage table columns.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_MANAGEMENT_MANAGE_COLUMNS,
      position: "left" as const,
    });

    console.log('User Management tour steps:', steps.length);
    setSteps(steps);

    // Auto-start tour if not completed
    if (!isTourCompleted && users.length > 0) {
      setOpenTour(true);
    }
  }, [setSteps, isTourCompleted, setTourPage, users, hasTourAccess]);

  const [showDeleteUserDialogState, setShowDeleteUserDialogState] = useState<{show: boolean, userID: string | null}>({
    show: false,
    userID: null,
  });
  const deleteUserConfirmed = async (confirmed: boolean): Promise<void> => {
    if (!confirmed) {
      setShowDeleteUserDialogState({show: false, userID: null});
      // console.log('[ASD]', `Delete user cancelled`);
      return;
    }
    // console.log('[ASD]', `Delete user confirmed!`, showDeleteUserDialogState.userID)
    await deleteUser(showDeleteUserDialogState.userID);
    setShowDeleteUserDialogState({show: false, userID: null});
  };

  // DED-1974: Fixed delete to show success toast and trigger page reload
  const deleteUser = async (userId: string | null) => {
    if (!userId) {
      toast.error('Invalid user ID');
      return;
    }

    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(`${import.meta.env.VITE_cloudFunctionURL}/deiterateInternalAPI/users/delete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Company-ID': activeTenancy.id,
        },
        body: JSON.stringify({ userID: userId, companyID: activeTenancy.id }),
      });
      
      if (response.ok) {
        toast.success('User deleted successfully');
        // Force page reload to refresh user list from context
        window.location.reload();
      } else {
        // Parse error response for better error messages
        let errorMessage = 'Failed to delete user';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // If response isn't JSON, use status text
          errorMessage = `Failed to delete user: ${response.statusText}`;
        }
        toast.error(errorMessage);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete user');
    }
  };

  const handleSyncEntraUsers = async () => {
    if (!activeTenancy?.id) return;
    
    setEntraLoading(true);
    try {
      const success = await entraUserService.triggerEntraSync(activeTenancy.id);
      if (success) {
        toast.success('Entra user sync triggered successfully');
        // Clear cache to force refresh on next fetch
        entraUserService.clearCache(activeTenancy.id);
        // Fetch updated data after a short delay
        setTimeout(async () => {
          const result = await entraUserService.getEntraUsers(activeTenancy.id, true);
          if (result) {
            setEntraUsers(result.users);
            setLastSyncTime(new Date(result.summary.syncedAt));
          }
        }, 2000);
      } else {
        toast.error('Failed to trigger Entra user sync');
      }
    } catch (error) {
      console.error('Error triggering Entra sync:', error);
      toast.error('Failed to trigger Entra user sync');
    } finally {
      setEntraLoading(false);
    }
  };

  // Define columns for table view
  // DED-1444: Conditionally include ISMS Role column based on plan
  const columns: ColumnDef<User>[] = React.useMemo(
    () => {
      const baseColumns: ColumnDef<User>[] = [
        {
          accessorKey: 'firstName',
          header: 'First Name',
          cell: ({ row }) => <div className="font-medium">{row.original.firstName}</div>,
        },
        {
          accessorKey: 'lastName',
          header: 'Last Name',
          cell: ({ row }) => <div className="font-medium">{row.original.lastName}</div>,
        },
        {
          accessorKey: 'email',
          header: 'Email',
          cell: ({ row }) => (
            <div className="flex items-center gap-2">
              <div className="text-muted-foreground">{row.original.email}</div>
              {(row.original.isEntraUser || row.original.hasEntraAccount) && (
                <Badge variant="secondary" className="text-xs">
                  Entra
                </Badge>
              )}
            </div>
          ),
        },
        {
          accessorKey: 'businessUnit',
          header: 'Business Unit',
          cell: ({ row }) => <div className="text-sm">{row.original.businessUnit || '-'}</div>,
        },
        ...(showEntraUsers ? [{
          accessorKey: 'accountEnabled',
          header: 'Account Status',
          cell: ({ row }) => (
            <div className="text-sm">
              {row.original.isEntraUser ? (
                row.original.accountEnabled ? (
                  <Badge variant="default" className="text-xs">Active</Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">Disabled</Badge>
                )
              ) : (
                '-'
              )}
            </div>
          ),
        }] : []),
      ];

      // DED-1444: Only add ISMS Role and Committee columns for non-Starter plans
      if (!isStarterPlan) {
        baseColumns.push({
          accessorKey: 'ismsRole',
          header: 'ISMS Role',
          cell: ({ row }) => <div className="text-sm">{row.original.ismsRole || '-'}</div>,
        });
        // DED-1514: Add ISMS Committee column
        baseColumns.push({
          accessorKey: 'ismsCommittee',
          header: 'Committee',
          cell: ({ row }) => <div className="text-sm">{row.original.ismsCommittee || '-'}</div>,
        });
      }

      // Add Entra Groups column when showing Entra users
      if (showEntraUsers) {
        baseColumns.push({
          accessorKey: 'entraGroups',
          header: 'Entra Groups',
          cell: ({ row }) => {
            const groups = row.original.entraGroups;
            if (!groups || groups.length === 0) {
              return <div className="text-sm">-</div>;
            }
            // Filter out "All Users" group
            const filteredGroups = groups.filter(g => g.name !== 'All Users');
            const [showAll, setShowAll] = React.useState(false);
            const displayGroups = showAll ? filteredGroups : filteredGroups.slice(0, 2);
            const remaining = filteredGroups.length - 2;
            
            return (
              <div className="flex flex-wrap gap-1">
                {displayGroups.map(group => (
                  <Badge key={group.id} variant="outline" className="text-xs">
                    {group.name}
                  </Badge>
                ))}
                {remaining > 0 && !showAll && (
                  <Badge 
                    variant="outline" 
                    className="text-xs cursor-pointer hover:bg-secondary"
                    onClick={() => setShowAll(true)}
                  >
                    +{remaining} more
                  </Badge>
                )}
                {showAll && filteredGroups.length > 2 && (
                  <Badge 
                    variant="outline" 
                    className="text-xs cursor-pointer hover:bg-secondary"
                    onClick={() => setShowAll(false)}
                  >
                    Show less
                  </Badge>
                )}
              </div>
            );
          },
        });
      }

      // Actions column always at the end
      baseColumns.push({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          // DED-1250: Check if this is an MSP user
          const isMspUser = row.original.isMspUser;
          
          if (isMspUser) {
            return (
              <div id={TOUR_STEP_IDS.USER_MANAGEMENT_TABLE_ACTIONS} className="flex gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => setEditMspUser(row.original)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>
            );
          }
          
          return (
            <div id={TOUR_STEP_IDS.USER_MANAGEMENT_TABLE_ACTIONS} className="flex gap-2">
              {/* Mobile edit button */}
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 sm:hidden"
                onClick={() => setEditDrawer(row.original)}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              {/* Desktop edit button */}
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 hidden sm:flex"
                onClick={() => setEditUser(row.original)}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                size="icon"
                variant="destructive"
                className="h-8 w-8"
                onClick={() => setShowDeleteUserDialogState({show: true, userID: row.original.id})}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          );
        },
      });

      return baseColumns;
    },
    [activeTenancy.id, isStarterPlan, showEntraUsers]
  );


  // DED-1250: Create MSP user entries from mspDetails
  const mspUsers = React.useMemo((): User[] => {
    const mspUserList: User[] = [];
    
    if (mspDetails?.techPartner?.msp) {
      mspUserList.push({
        id: `msp-tech-${mspDetails.techPartner.msp}`,
        email: '',
        firstName: 'Tech Partner',
        lastName: 'MSP',
        roles: {},
        isMspUser: true,
        mspType: 'techPartner',
      });
    }
    
    if (mspDetails?.businessPartner?.msp) {
      mspUserList.push({
        id: `msp-business-${mspDetails.businessPartner.msp}`,
        email: '',
        firstName: 'Business Partner',
        lastName: 'MSP',
        roles: {},
        isMspUser: true,
        mspType: 'businessPartner',
      });
    }
    
    return mspUserList;
  }, [mspDetails]);

  const filteredUsers = React.useMemo(() => {
    console.log('OG Users:', users)
    console.log('Entra Users Count:', entraUsers.length)
    console.log('Show Entra Users:', showEntraUsers)
    // Filter regular users (exclude any users with "MSP" in their name to avoid duplicates)
    // DED-605: Removed team from search filter - Team Settings should not be in User Management
    // Also exclude users that will be shown as Entra users to avoid duplicates
    const entraEmails = new Set(entraUsers.map(u => u.email.toLowerCase()));
    const regularUsers = users.filter((u) => {
      const match = `${u.firstName} ${u.lastName} ${u.email} ${u.businessUnit || ''}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      // Exclude any user with "MSP" in firstName or lastName (case-insensitive)
      const hasMspInName = 
        u.firstName?.toLowerCase().includes('msp') || 
        u.lastName?.toLowerCase().includes('msp');
      // Exclude users that are in the Entra list when showing Entra users
      const isEntraUser = showEntraUsers && entraEmails.has(u.email.toLowerCase());
      return match && !hasMspInName && !isEntraUser;
    }).map(u => {
      // Mark users that exist in Entra even when not showing Entra users
      if (entraEmails.has(u.email.toLowerCase())) {
        return { ...u, hasEntraAccount: true };
      }
      return u;
    });
    
    // DED-1250: Include MSP users if they match the search term
    const filteredMspUsers = mspUsers.filter((u) => {
      if (!searchTerm) return true;
      return `${u.firstName} ${u.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    });
    
    // Process Entra users if toggle is on
    let processedEntraUsers: User[] = [];
    if (showEntraUsers) {
      const matchedEntraUsers = entraUserService.matchEntraUsersWithExisting(entraUsers, users);
      processedEntraUsers = matchedEntraUsers.map(entraUser => {
        const existingUser = users.find(u => u.email.toLowerCase() === entraUser.email.toLowerCase());
        return {
          id: entraUser.entraId,
          email: entraUser.email,
          firstName: entraUser.firstName,
          lastName: entraUser.lastName,
          roles: existingUser?.roles || {},
          businessUnit: entraUser.department || undefined,
          isEntraUser: true,
          entraId: entraUser.entraId,
          lastSyncedAt: entraUser.lastSyncedAt,
          accountEnabled: entraUser.accountEnabled,
          isMspUser: false,
          entraGroups: entraUser.groups || [],
          // If user has matching de.iterate account, include their ISMS fields
          ismsRole: existingUser?.ismsRole,
          ismsCommittee: existingUser?.ismsCommittee,
        };
      });
      
      // Filter Entra users by search term
      if (searchTerm) {
        processedEntraUsers = processedEntraUsers.filter((u) => {
          return `${u.firstName} ${u.lastName} ${u.email}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        });
      }
    }
    
    // Combine all users - put Entra users first so they're visible on page 1
    const allUsers = [...processedEntraUsers, ...regularUsers, ...filteredMspUsers];
    
    console.log('Processed Entra Users Count:', processedEntraUsers.length);
    console.log('All Users Count:', allUsers.length);
    
    // Pagination logic (only for card view, table view handles its own pagination)
    const totalPages = Math.ceil(allUsers.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedUsers = allUsers.slice(startIndex, endIndex);

    return {
      allUsers, // Full list for table view
      paginatedUsers, // Paginated list for card view
      totalPages,
    };
  }, [users, mspUsers, entraUsers, showEntraUsers, searchTerm, currentPage, pageSize]);

  // Reset page if search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Reset page if total pages is less than current page
  React.useEffect(() => {
    const { totalPages } = filteredUsers;
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [filteredUsers.totalPages, currentPage]);

  return (
    <div id={TOUR_STEP_IDS.USER_MANAGEMENT_PAGE} className="p-6 glass-card">
      <div className="flex justify-between mb-6 items-center">
        <h1 className="text-2xl font-semibold">Users</h1>
        <div className="hidden sm:block">
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogTrigger asChild>
              <Button id={TOUR_STEP_IDS.USER_MANAGEMENT_ADD_USER}><UserPlus2 className="w-4 h-4 mr-2" /> Add User</Button>
            </DialogTrigger>
            <DialogContent 
              className="max-w-3xl w-3xl max-h-[90vh] bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
                {/* Close button */}
                <button
                  onClick={() => setShowAddModal(false)}
                  className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
                </button>
                
                {/* Header */}
                <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
                    <FontAwesomeIcon icon={faUserPlus} className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">Add New User</h2>
                  <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                    Create a new user account and assign roles and permissions
                  </p>
                </div>

                {/* Form Content - scrollable */}
                <div className="p-8 flex-1 min-h-0 overflow-y-auto">
                  <UserForm
                    plan={activeTenancy.plan}
                    isDrawer={false}
                    companyId={activeTenancy.id}
                    onSuccess={() => setShowAddModal(false)}
                  />
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="sm:hidden">
          <Drawer open={showAddDrawer} onOpenChange={setShowAddDrawer}>
            <DrawerTrigger asChild>
              <Button><UserPlus2 className="w-4 h-4 mr-2" /> Add User</Button>
            </DrawerTrigger>
            <DrawerContent className="bg-card/95 backdrop-blur-xl px-4 py-6 mt-0 max-h-[90vh]">
              <div className="flex flex-col h-full max-h-[80vh]">
                {/* Header */}
                <div className="pb-6 mb-4 border-b border-white/10 text-center shrink-0">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
                    <FontAwesomeIcon icon={faUserPlus} className="w-7 h-7 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">Add New User</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Create a new user account and assign roles
                  </p>
                </div>

                {/* Form Content - scrollable */}
                <div className="flex-1 overflow-y-auto min-h-0 pb-20">
                  <UserForm
                    plan={activeTenancy.plan}
                    isDrawer={true}
                    companyId={activeTenancy.id}
                    onSuccess={() => setShowAddDrawer(false)}
                  />
                </div>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>

      {/* Entra Secret Expiry Warning Banner */}
      {secretExpiry && (secretExpiry.isExpired || secretExpiry.isExpiringSoon) && (
        <div className={`mb-4 p-4 rounded-lg border ${
          secretExpiry.isExpired 
            ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' 
            : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
        }`}>
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 ${secretExpiry.isExpired ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
              {secretExpiry.isExpired ? (
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${secretExpiry.isExpired ? 'text-red-900 dark:text-red-100' : 'text-yellow-900 dark:text-yellow-100'}`}>
                {secretExpiry.isExpired ? 'Entra Client Secret Expired' : 'Entra Client Secret Expiring Soon'}
              </h3>
              <p className={`text-sm mt-1 ${secretExpiry.isExpired ? 'text-red-800 dark:text-red-200' : 'text-yellow-800 dark:text-yellow-200'}`}>
                {secretExpiry.isExpired ? (
                  <>Your Microsoft Entra client secret has expired. User sync will fail until you update the secret.</>
                ) : (
                  <>Your Microsoft Entra client secret will expire in <strong>{secretExpiry.daysUntilExpiry} days</strong> ({new Date(secretExpiry.secretExpiryDate!).toLocaleDateString()}). Please update it soon to avoid service interruption.</>
                )}
              </p>
              <div className="mt-2">
                <a 
                  href="https://portal.azure.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`text-sm font-medium underline ${secretExpiry.isExpired ? 'text-red-700 dark:text-red-300' : 'text-yellow-700 dark:text-yellow-300'}`}
                >
                  Update in Azure Portal →
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Input
            id={TOUR_STEP_IDS.USER_MANAGEMENT_SEARCH_BAR}
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64 hover:bg-transparent focus:bg-transparent"
          />
          
          {/* DED-1869: Entra Users Toggle - only show if integration is enabled */}
          {isEntraIntegrationEnabled && (
          <div id={TOUR_STEP_IDS.USER_MANAGEMENT_SHOW_ENTRA_TOGGLE} className="flex items-center gap-2">
            <Switch
              checked={showEntraUsers}
              onCheckedChange={setShowEntraUsers}
            />
            <span className="text-sm font-medium">Show Entra Users</span>
            {showEntraUsers && (
              <Button
                id={TOUR_STEP_IDS.USER_MANAGEMENT_SYNC_BUTTON}
                variant="outline"
                size="sm"
                onClick={handleSyncEntraUsers}
                disabled={entraLoading}
                className="ml-2"
              >
                {entraLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Shield className="w-4 h-4 mr-2" />
                )}
                Sync
              </Button>
            )}
            {lastSyncTime && showEntraUsers && (
              <span className="text-xs text-muted-foreground ml-2">
                Last sync: {lastSyncTime.toLocaleString()}
              </span>
            )}
            {secretExpiry && showEntraUsers && secretExpiry.secretExpiryDate && (
              <div className="ml-2 flex items-center gap-1">
                <span className="text-xs text-muted-foreground">•</span>
                <span className={`text-xs ${
                  secretExpiry.isExpired 
                    ? 'text-red-600 dark:text-red-400 font-semibold' 
                    : secretExpiry.isExpiringSoon 
                      ? 'text-yellow-600 dark:text-yellow-400 font-semibold' 
                      : 'text-muted-foreground'
                }`}>
                  Secret expires: {new Date(secretExpiry.secretExpiryDate).toLocaleDateString()}
                  {secretExpiry.isExpired && ' (EXPIRED)'}
                  {secretExpiry.isExpiringSoon && !secretExpiry.isExpired && ` (${secretExpiry.daysUntilExpiry} days)`}
                </span>
              </div>
            )}
          </div>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div className="inline-flex items-center border rounded-lg p-1 bg-muted/50">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className="h-8"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="h-8"
            >
              <Table2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex gap-2 text-muted-foreground">
          <Loader2 className="animate-spin h-4 w-4" /> Loading users...
        </div>
      ) : viewMode === 'table' ? (
        <DataTable
          columns={columns}
          data={filteredUsers.allUsers}
          pageSize={pageSize}
        />
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"> */}
            {filteredUsers.paginatedUsers.map((user) => (
              <div key={user.id} className="border rounded-md p-4 flex flex-col justify-between bg-white dark:bg-secondary">
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    {user.firstName} {user.lastName}
                    {user.isEntraUser && (
                      <Badge variant="secondary" className="text-xs">
                        Entra
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">{user.email}</div>
                  {/* DED-605: Removed team display - Team Settings should not be in User Management */}
                  {user.businessUnit && (
                    <div className="text-sm text-muted-foreground">{user.businessUnit}</div>
                  )}
                  {user.isEntraUser && (
                    <div className="text-sm">
                      {user.accountEnabled ? (
                        <Badge variant="default" className="text-xs">Active</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Disabled</Badge>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex justify-end mt-4 gap-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="delete-user-btn"
                    onClick={() => deleteUser(user.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Button className="sm:hidden" size="icon" onClick={() => setEditDrawer(user)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button className="hidden sm:flex" size="icon" onClick={() => setEditUser(user)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination controls - only show in card view */}
          {viewMode === 'cards' && (
            <div className="mt-4 flex items-center justify-between">
              {/* Spacer for alignment */}
              <div className="w-32" />

              {/* Pagination buttons - centered */}
              <div className="flex gap-2">
                {(() => {
                  const pages: (number | string)[] = [];

                  // Always show first page
                  pages.push(1);

                  if (filteredUsers.totalPages <= 7) {
                    // Show all pages if 7 or fewer
                    for (let i = 2; i <= filteredUsers.totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Smart pagination with ellipsis
                    if (currentPage > 3) {
                      pages.push('ellipsis-start');
                    }

                    // Show pages around current page
                    const start = Math.max(2, currentPage - 1);
                    const end = Math.min(filteredUsers.totalPages - 1, currentPage + 1);

                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }

                    if (currentPage < filteredUsers.totalPages - 2) {
                      pages.push('ellipsis-end');
                    }

                    // Always show last page
                    pages.push(filteredUsers.totalPages);
                  }

                  return pages.map((page) => {
                    if (typeof page === 'string') {
                      return (
                        <span key={page} className="flex items-center px-2 text-muted-foreground">
                          ...
                        </span>
                      );
                    }

                    return (
                      <Button
                        key={page}
                        variant={page === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    );
                  });
                })()}
              </div>

              {/* Per page selector - right aligned */}
              <div id={TOUR_STEP_IDS.USER_MANAGEMENT_RESULTS_SELECTOR} className="flex items-center gap-2">
                <Label>Per page</Label>
                <Select value={String(pageSize)} onValueChange={(v) => {
                  setPageSize(Number(v));
                  setCurrentPage(1);
                }}>
                  <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </>
      )}

      <div className="hidden sm:block">
        <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
          <DialogContent 
            className="max-w-3xl! w-3xl! max-h-[90vh] bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
              {/* Close button */}
              <button
                onClick={() => setEditUser(null)}
                className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
              </button>
              
              {/* Header */}
              <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
                  <FontAwesomeIcon icon={faUser} className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">Edit User</h2>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                  Update user information, roles, and permissions
                </p>
              </div>

              {/* Form Content - scrollable */}
              <div className="p-8 flex-1 min-h-0 overflow-y-auto">
                {editUser && (
                  <UserForm
                    isDrawer={false}
                    editData={editUser}
                    companyId={activeTenancy.id}
                    onSuccess={() => setEditUser(null)}
                    plan={activeTenancy.plan}
                  />
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="sm:hidden">
        <Drawer open={!!editDrawer} onOpenChange={(open) => !open && setEditDrawer(null)}>
          <DrawerContent className="bg-card/95 backdrop-blur-xl px-4 py-6 mt-0 max-h-[90vh]">
            <div className="flex flex-col h-full max-h-[80vh]">
              {/* Header */}
              <div className="pb-6 mb-4 border-b border-white/10 text-center shrink-0">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
                  <FontAwesomeIcon icon={faUser} className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Edit User</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Update user information, roles, and permissions
                </p>
              </div>

              {/* Form Content - scrollable */}
              <div className="flex-1 overflow-y-auto min-h-0 pb-20">
                {editDrawer && (
                  <UserForm
                    isDrawer={true}
                    editData={editDrawer}
                    companyId={activeTenancy.id}
                    onSuccess={() => setEditDrawer(null)}
                    plan={activeTenancy.plan}
                  />
                )}
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      </div>
      <DeleteUserConfirmationDialog callback={(confirmed) => deleteUserConfirmed(confirmed)} show={showDeleteUserDialogState.show} />
      
      {/* DED-1250: MSP Edit Dialog */}
      <MspEditDialog
        open={!!editMspUser}
        onOpenChange={(open) => !open && setEditMspUser(null)}
        mspUser={editMspUser}
        mspDetails={mspDetails}
        companyId={activeTenancy.id}
        onSuccess={async () => {
          setEditMspUser(null);
          // Refresh MSP details
          try {
            const { getDoc, doc } = await import('firebase/firestore');
            const customerRef = doc(db, 'customers', activeTenancy.id);
            const customerSnap = await getDoc(customerRef);
            if (customerSnap.exists()) {
              const data = customerSnap.data();
              setMspDetails(data.mspDetails || null);
            }
          } catch (error) {
            console.error('Error refreshing MSP details:', error);
          }
        }}
      />
      {hasTourAccess && (
        <TourAlertDialog 
          isOpen={openTour} 
          setIsOpen={setOpenTour} 
        />
      )}
    </div>
  );
}

// DED-1250: MSP Edit Dialog Component
interface MspEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mspUser: User | null;
  mspDetails: MspDetails | null;
  companyId: string;
  onSuccess: () => void;
}

function MspEditDialog({ open, onOpenChange, mspUser, mspDetails, companyId, onSuccess }: MspEditDialogProps) {
  const [saving, setSaving] = useState(false);
  const [mspName, setMspName] = useState<string>('');
  const [loadingName, setLoadingName] = useState(false);
  
  // Get the MSP partner details based on the user type
  const mspType = mspUser?.mspType;
  const partnerDetails = mspType === 'techPartner' 
    ? mspDetails?.techPartner 
    : mspDetails?.businessPartner;
  
  const [impersonationAllowed, setImpersonationAllowed] = useState(partnerDetails?.impersonationAllowed ?? false);
  
  // Fetch MSP name when dialog opens
  useEffect(() => {
    const fetchMspName = async () => {
      if (!partnerDetails?.msp || !open) return;
      
      setLoadingName(true);
      try {
        const details = await mspService.getMspDetails(partnerDetails.msp, companyId);
        setMspName(details.customerFriendlyName || details.customerName || 'Unknown MSP');
      } catch (error) {
        console.error('Error fetching MSP name:', error);
        setMspName('Unknown MSP');
      } finally {
        setLoadingName(false);
      }
    };
    
    fetchMspName();
  }, [partnerDetails?.msp, companyId, open]);
  
  // Reset impersonation state when dialog opens
  useEffect(() => {
    if (open && partnerDetails) {
      setImpersonationAllowed(partnerDetails.impersonationAllowed ?? false);
    }
  }, [open, partnerDetails]);
  
  const handleSave = async () => {
    if (!mspType || !partnerDetails) return;
    
    setSaving(true);
    try {
      const { updateDoc, doc } = await import('firebase/firestore');
      const customerRef = doc(db, 'customers', companyId);
      
      await updateDoc(customerRef, {
        [`mspDetails.${mspType}.impersonationAllowed`]: impersonationAllowed,
      });
      
      toast.success('MSP access settings updated successfully');
      onSuccess();
    } catch (error) {
      console.error('Error updating MSP settings:', error);
      toast.error('Failed to update MSP settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleRemoveMsp = async () => {
    if (!mspType || !partnerDetails) return;
    
    if (!confirm(`Are you sure you want to remove this ${mspType === 'techPartner' ? 'Tech Partner' : 'Business Partner'} MSP? This action cannot be undone.`)) {
      return;
    }
    
    setSaving(true);
    try {
      const { updateDoc, doc, deleteField } = await import('firebase/firestore');
      const customerRef = doc(db, 'customers', companyId);
      
      await updateDoc(customerRef, {
        [`mspDetails.${mspType}`]: deleteField(),
      });
      
      toast.success('MSP removed successfully');
      onSuccess();
    } catch (error) {
      console.error('Error removing MSP:', error);
      toast.error('Failed to remove MSP');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-white dark:bg-secondary">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {mspType === 'techPartner' ? 'Tech Partner' : 'Business Partner'} MSP Settings
          </DialogTitle>
          <DialogDescription>
            Manage access settings for this MSP partner.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          {/* MSP Name */}
          <div className="grid gap-2">
            <Label>MSP Organization</Label>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              {loadingName ? (
                <span className="text-muted-foreground">Loading...</span>
              ) : (
                <span className="font-medium">{mspName}</span>
              )}
            </div>
          </div>
          
          {/* Impersonation Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-0.5">
              <Label className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Allow Impersonation
              </Label>
              <p className="text-sm text-muted-foreground">
                When enabled, this MSP can access and manage your tenancy.
              </p>
            </div>
            <Switch
              checked={impersonationAllowed}
              onCheckedChange={setImpersonationAllowed}
              disabled={saving}
            />
          </div>
          
          {/* Warning when impersonation is enabled */}
          {impersonationAllowed && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-500 mt-0.5 shrink-0" />
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                This MSP will have access to view and manage your organization's data.
              </p>
            </div>
          )}
        </div>
        
        <div className="flex justify-between gap-2">
          <Button
            variant="destructive"
            onClick={handleRemoveMsp}
            disabled={saving}
          >
            Remove MSP
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface FormProps {
  companyId: string;
  onSuccess: () => void;
  editData?: User;
  isDrawer?: boolean;
  plan?: string; // DED-1444: Plan to determine if ISMS fields should be shown
}

function UserForm({ companyId, onSuccess, editData, isDrawer, plan }: FormProps) {
  // DED-1444: Hide ISMS Role and Committee for Starter plans (PA, E8, SMB, DISP)
  const isStarterPlan = plan?.toLowerCase().includes('starter');
  // DED-605: Include loading state to handle async data loading
  // DED-605: Removed teams - Team Settings should not be in User Management
  const { businessUnits, loading: orgStructureLoading } = useOrgStructure();
  const [saving, setSaving] = useState(false);
  // DED-1894: Error dialog state for themed error messages
  const [errorDialog, setErrorDialog] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  // DED-605: Removed team field - Team Settings should not be in User Management
  const [form, setForm] = useState({
    email: editData?.email || '',
    firstName: editData?.firstName || '',
    lastName: editData?.lastName || '',
    roles: editData?.roles || { user: true }, // Default to user role for new users
    ismsRole: editData?.ismsRole || 'User',
    ismsCommittee: editData?.ismsCommittee || 'none',
    businessUnit: editData?.businessUnit || '',
    assignPolicies: true,
  });

  const [originalEmail] = useState(editData?.email || '');

  const updateField = (name: string, value: string | boolean) => {
    setForm((f) => ({ ...f, [name]: value }));
  };

  const updateRole = (role: string, enabled: boolean) => {
    setForm((f) => ({
      ...f,
      roles: {
        ...f.roles,
        [role]: enabled
      }
    }));
  };

  const updateIsmsRole = (role: string) => {
    setForm((f) => ({ ...f, ismsRole: role }));
  };

  const updateIsmsCommittee = (committee: string) => {
    setForm((f) => ({ ...f, ismsCommittee: committee }));
  };

  const [rolesOpen, setRolesOpen] = useState(false);

  const generatePassword = (length = 15) => {
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+[]{}|;:',.<>?";

    function generate() {
      let password = '';
      for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * charset.length);
        password += charset[randomIndex];
      }
      return password;
    }

    function validate(password: string) {
      const hasUppercase = /[A-Z]/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSpecialChar = /[!@#$%^&*()_+\[\]{}|;:',.<>?]/.test(password);

      return password.length >= length && hasUppercase && hasLowercase && hasNumber && hasSpecialChar;
    }

    let password: string;
    do {
      password = generate();
    } while (!validate(password));

    return password;
  };

  const saveUser = async () => {
    setSaving(true);

    try {
      if (editData) {
        // Existing user update flow
        await setDoc(doc(db, 'users', editData.id), {
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          roles: form.roles,
        }, { merge: true });

        if (form.email !== originalEmail) {
          const token = await auth.currentUser?.getIdToken();
          await fetch(`${import.meta.env.VITE_cloudFunctionURL}/deiterateInternalAPI/updateUserEmail`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Company-ID': companyId,
            },
            body: JSON.stringify({ newEmail: form.email, userID: editData.id }),
          });
        }

        // DED-605: Removed teamName - Team Settings should not be in User Management
        await setDoc(doc(db, 'customers', companyId), {
          customerOrg: {
            users: {
              [editData.id]: {
                businessUnit: form.businessUnit,
                ismsRole: form.ismsRole,
                ismsCommittee: form.ismsCommittee,
                lastUpdatedBy: auth.currentUser?.uid,
                roles: form.roles,
                userID: editData.id,
              },
            },
          },
        }, { merge: true });

        // User data will be automatically updated via the TenancyContext listener

        onSuccess();
      } else {
        // New user creation flow
        const password = generatePassword(15);

        const authWorkerApp = firebaseImport.initializeApp(app.options, 'auth-worker');
        const authWorkerAuth = firebaseImport.auth(authWorkerApp);

        // DO NOT await here if using firebase v8-style
        authWorkerAuth.setPersistence(firebaseAuth.Auth.Persistence.NONE);

        const userCredential = await authWorkerAuth.createUserWithEmailAndPassword(form.email, password);
        const newUID = userCredential.user.uid;
        const userEmail = userCredential.user.email;

        const idToken = await auth.currentUser?.getIdToken();

        const response = await fetch(`${import.meta.env.VITE_cloudFunctionURL}/deiterateInternalAPI/organisations/addUser`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
            'Company-ID': companyId,
          },
          // DED-605: Removed team - Team Settings should not be in User Management
          body: JSON.stringify({
            firebaseUID: newUID,
            email: userEmail,
            firstName: form.firstName,
            lastName: form.lastName,
            companyID: companyId,
            roles: form.roles,
            PAUser: false,
            assignPolicies: form.assignPolicies,
            businessUnit: form.businessUnit,
            committeeRole: form.ismsCommittee,
            ismsRole: form.ismsRole,
            addedBy: auth.currentUser?.uid,
            preserveMspRole: true,
          }),
        });

        if (!response.ok) {
          throw new Error(await response.text());
        }

        // DED-605: Removed teamName - Team Settings should not be in User Management
        await setDoc(doc(db, 'customers', companyId), {
          customerOrg: {
            users: {
              [newUID]: {
                businessUnit: form.businessUnit,
                ismsRole: form.ismsRole,
                ismsCommittee: form.ismsCommittee,
                lastUpdatedBy: auth.currentUser?.uid,
                roles: form.roles,
                userID: newUID,
              },
            },
          },
        }, { merge: true });

        // User data will be automatically updated via the TenancyContext listener

        await authWorkerAuth.signOut();
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error saving user:', err);
      setErrorDialog({ show: true, message: err.message || 'An unknown error occurred' });
    } finally {
      setSaving(false);
    }
  };



  return (
    <div className="space-y-6">
      {/* Basic Information Card */}
      <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
        <div className="flex items-start gap-4 mb-5">
          <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
            <FontAwesomeIcon icon={faUser} className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground mb-1">Basic Information</h3>
            <p className="text-xs text-muted-foreground">
              User's personal details and contact information
            </p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-sm font-medium">First Name</Label>
              <Input 
                id="firstName" 
                value={form.firstName} 
                onChange={(e) => updateField('firstName', e.target.value)}
                className="bg-white/5 border-primary/10 h-11"
                placeholder="Enter first name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-sm font-medium">Last Name</Label>
              <Input 
                id="lastName" 
                value={form.lastName} 
                onChange={(e) => updateField('lastName', e.target.value)}
                className="bg-white/5 border-primary/10 h-11"
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
            <Input 
              id="email" 
              value={form.email} 
              onChange={(e) => updateField('email', e.target.value)} 
              type="email"
              className="bg-white/5 border-primary/10 h-11"
              placeholder="user@example.com"
            />
          </div>
        </div>
      </div>

      {/* Business Unit Card */}
      <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
        <div className="flex items-start gap-4 mb-5">
          <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
            <FontAwesomeIcon icon={faBuilding} className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground mb-1">Organization</h3>
            <p className="text-xs text-muted-foreground">
              Assign user to a business unit
            </p>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="businessUnit" className="text-sm font-medium">Business Unit</Label>
          <Select value={form.businessUnit} onValueChange={(v) => updateField('businessUnit', v)} disabled={orgStructureLoading}>
            <SelectTrigger id="businessUnit" className="w-full bg-white/5 border-primary/10 h-11">
              <SelectValue placeholder={orgStructureLoading ? "Loading..." : "Select business unit"} />
            </SelectTrigger>
            <SelectContent>
              {businessUnits.map((b) => (
                <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!orgStructureLoading && businessUnits.length === 0 && (
            <p className="text-xs text-muted-foreground mt-2">No business units defined for this organisation.</p>
          )}
        </div>
      </div>

      {/* Roles & Permissions Card */}
      <div className="rounded-xl border border-primary/20 bg-linear-to-br from-primary/5 to-primary/15 backdrop-blur-sm p-6">
        <div className="flex items-start gap-4 mb-5">
          <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
            <FontAwesomeIcon icon={faUserShield} className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground mb-1">Roles & Permissions</h3>
            <p className="text-xs text-muted-foreground">
              Configure user access levels and system permissions
            </p>
          </div>
        </div>
        
        <div className="grid gap-2">
        <Collapsible open={rolesOpen} onOpenChange={setRolesOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium">Roles & Permissions</span>
                <span className="text-xs text-muted-foreground">
                  {Object.values(form.roles || {}).filter(Boolean).length + 
                   (form.ismsRole !== 'User' ? 1 : 0) + 
                   (form.ismsCommittee !== 'none' ? 1 : 0)} selected
                </span>
              </div>
              <ChevronDown className={`h-4 w-4 transition-transform ${rolesOpen ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-4 border rounded-lg p-4">
            {/* de.iterate Roles */}
            <div>
              <div className="text-sm font-semibold text-muted-foreground mb-3">de.iterate Roles</div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">Company Admin</span>
                    <span className="text-xs text-muted-foreground">(Full access)</span>
                  </div>
                  <Switch
                    checked={form.roles?.owner || false}
                    onCheckedChange={(checked) => updateRole('owner', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">Academy User</span>
                    <span className="text-xs text-muted-foreground">(Tour access)</span>
                  </div>
                  <Switch
                    checked={form.roles?.academy || false}
                    onCheckedChange={(checked) => updateRole('academy', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">Policy Reader</span>
                    <span className="text-xs text-muted-foreground">(Read-only)</span>
                  </div>
                  <Switch
                    checked={form.roles?.user || false}
                    onCheckedChange={(checked) => updateRole('user', checked)}
                  />
                </div>
                
                {/* Custom roles */}
                {Object.keys(form.roles || {})
                  .filter(role => !['owner', 'academy', 'user'].includes(role))
                  .map(customRole => (
                    <div key={customRole} className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium capitalize">{customRole}</span>
                        <span className="text-xs text-muted-foreground">(Custom)</span>
                      </div>
                      <Switch
                        checked={form.roles?.[customRole] || false}
                        onCheckedChange={(checked) => updateRole(customRole, checked)}
                      />
                    </div>
                  ))}
              </div>
            </div>
            
            {/* ISMS Roles - only show if not starter plan */}
            {!isStarterPlan && (
              <>
                <div className="border-t pt-4">
                  <div className="text-sm font-semibold text-muted-foreground mb-3">MMS Roles</div>
                  
                  <div className="space-y-4">
                    {/* MMS Role Radio Group */}
                    <div className="space-y-3">
                      <Label className="text-sm">MMS Role</Label>
                      <div className="space-y-2">
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => updateIsmsRole('User')}
                        >
                          <span className="text-sm">User</span>
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            form.ismsRole === 'User' ? 'border-primary' : 'border-input'
                          }`}>
                            {form.ismsRole === 'User' && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                        </div>
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => updateIsmsRole('Asset Owner')}
                        >
                          <span className="text-sm">Asset Owner</span>
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            form.ismsRole === 'Asset Owner' ? 'border-primary' : 'border-input'
                          }`}>
                            {form.ismsRole === 'Asset Owner' && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                        </div>
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => updateIsmsRole('Business Sponsor')}
                        >
                          <span className="text-sm">Business Sponsor</span>
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            form.ismsRole === 'Business Sponsor' ? 'border-primary' : 'border-input'
                          }`}>
                            {form.ismsRole === 'Business Sponsor' && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* MMS Committee Radio Group */}
                    <div className="space-y-3">
                      <Label className="text-sm">MMS Committee</Label>
                      <div className="space-y-2">
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => updateIsmsCommittee('none')}
                        >
                          <span className="text-sm">none</span>
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            form.ismsCommittee === 'none' ? 'border-primary' : 'border-input'
                          }`}>
                            {form.ismsCommittee === 'none' && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                        </div>
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => updateIsmsCommittee('Chair')}
                        >
                          <span className="text-sm">Chair</span>
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            form.ismsCommittee === 'Chair' ? 'border-primary' : 'border-input'
                          }`}>
                            {form.ismsCommittee === 'Chair' && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                        </div>
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => updateIsmsCommittee('Member')}
                        >
                          <span className="text-sm">Member</span>
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            form.ismsCommittee === 'Member' ? 'border-primary' : 'border-input'
                          }`}>
                            {form.ismsCommittee === 'Member' && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CollapsibleContent>
        </Collapsible>
        </div>
      </div>

      {/* Policy Assignment */}
      {!editData && (
        <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5">
          <div className="flex items-center gap-3">
            <Checkbox
              id="assignPolicies"
              checked={form.assignPolicies}
              onCheckedChange={(checked) => updateField('assignPolicies', !!checked)}
            />
            <div>
              <Label htmlFor="assignPolicies" className="text-sm font-medium cursor-pointer">Assign all policies</Label>
              <p className="text-xs text-muted-foreground">Automatically assign all existing policies to this user</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions - Footer style matching AddProcessDialog */}
      {!isDrawer && (
        <div className="border-t border-white/10 pt-5 mt-6 flex justify-between items-center">
          <p className="text-xs text-muted-foreground">
            {!form.firstName.trim() || !form.lastName.trim()
              ? "Enter user details to continue" 
              : !form.email.trim()
                ? "Enter email address to continue"
                : "Ready to save user"}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onSuccess} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={saveUser} disabled={saving} className="min-w-[100px]">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editData ? 'Save Changes' : 'Create User'}
            </Button>
          </div>
        </div>
      )}

      {/* Drawer Actions */}
      {isDrawer && (
        <div className="pt-6 flex justify-center">
          <div className="flex w-full max-w-md gap-1">
            <Button
              type="button"
              variant="outline"
              onClick={onSuccess}
              className="w-1/2"
            >
              Cancel
            </Button>
            <Button
              onClick={saveUser}
              disabled={saving}
              className="w-1/2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editData ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      )}

      {/* Error Dialog */}
      <Dialog open={errorDialog.show} onOpenChange={(open) => !open && setErrorDialog({ show: false, message: '' })}>
        <DialogContent className="sm:max-w-[425px] bg-white dark:bg-secondary">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Failed to Save User
            </DialogTitle>
            <DialogDescription className="pt-2">
              {errorDialog.message}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button onClick={() => setErrorDialog({ show: false, message: '' })}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type deleteUserConfirmationDialogProps = {
  show: boolean;
  callback: (confirmed: boolean) => void;
};
const DeleteUserConfirmationDialog = ({ show, callback }: deleteUserConfirmationDialogProps) => {
  return (
    <Dialog open={show}>
      <DialogContent className="bg-card">
        <DialogHeader>
          <DialogTitle>Delete User</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this user?
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => callback(false) }>Cancel</Button>
          <Button
            variant="destructive"
            onClick={() => callback(true)}
          >
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
